import { adobeService } from "@/app";
import { useUserDataResource } from "@/resources";
import { useAuth } from "@webmotors/consumer-shared-auth";
import { StringFormat } from "@packages/common";
import { SitePlatform } from "@packages/domain";
import { document, window } from "@packages/global";
import { useCallback, useMemo } from "react";
import type { DataLayerOptions, FavoriteOffVehicle } from "./use-adobe-data-layer.types";

export function useAdobeDataLayer() {
  const { user, token } = useAuth();

  const isMobile = useMemo(
    (): boolean => /Mobi/.test(window?.navigator?.userAgent),
    []
  );

  const platform = useMemo(
    (): SitePlatform => (isMobile ? SitePlatform.MOBILE : SitePlatform.DESKTOP),
    [isMobile]
  );

  const authUser = useMemo(() => {
    if (!user || !token) return null;
    return {
      id: user.sub,
      name: user.name,
      email: user.email,
      phoneNumber: "",
      authenticationToken: token,
    };
  }, [user, token]);

  const { data: userData, isFetched: isUserDataFetched } = useUserDataResource({
    user: authUser,
    enabled: !!authUser,
  });

  // When logged out there is no user node to enrich, so it is "ready" right away.
  const isUserDataReady = !authUser || isUserDataFetched;

  // Mirrors the garagem flow (withAdobeLaunch): Id/Sexo/DataNascimento/Cidade/Uf
  // come from the PF "dados-usuario" API, not from the JWT (the token carries
  // neither gender nor birthdate).
  const userNode = useMemo(() => {
    if (!userData) return undefined;

    return {
      userID: String(userData.Id ?? ""),
      gender: userData.Sexo === "F" ? "2" : "1",
      birthDate: userData.DataNascimento
        ? new Date(userData.DataNascimento)
            .toISOString()
            .replace(/-/g, "/")
            .substring(0, 10)
        : undefined,
      location: {
        city: userData.Cidade
          ? StringFormat.formatSlug(userData.Cidade)
          : undefined,
        state: userData.Uf ? userData.Uf.toLowerCase() : undefined,
      },
    };
  }, [userData]);

  const buildDataLayer = useCallback(
    (eventType: string, options: DataLayerOptions = {}) => {
      const params = {
        ...(options.dataLayer ?? {}),
        site: {
          environment: "servicos",
          subEnvironment: "garagem",
          tmsVersion: "S32",
          domain: document?.domain ?? "www.webmotors.com.br",
          platform,
          ...(options.dataLayer?.site ?? {}),
        },
        page: {
          flowType: "garagem",
          pageName: "/webmotors/garagem/favoritos",
          pageNameTier1: "garagem",
          pageNameTier2: "favoritos",
          pageType: "favoritos",
          url: window?.location?.href,
          trackingCode: {
            idcmp: new URLSearchParams(window?.location?.search).get("idcmp") ?? "",
            idcmpint: new URLSearchParams(window?.location?.search).get("idcmpint") ?? "",
            icid: new URLSearchParams(window?.location?.search).get("icid") ?? "",
            lkid: new URLSearchParams(window?.location?.search).get("lkid") ?? "",
          },
          ...(options.dataLayer?.page ?? {}),
        },
      };

      const objDataLayer = adobeService.createDataLayerObject({
        params,
        eventType,
        user: authUser,
      });

      // Match the garagem user node: drop the shared `pii` block and enrich with
      // the PF dados-usuario fields (userID/gender/birthDate/location). The data
      // layer `user` node is dynamic, so it is reassigned through a loose alias.
      const dataLayer = objDataLayer as { user?: Record<string, unknown> };
      if (dataLayer.user) {
        const loginStatus = dataLayer.user.loginStatus;
        dataLayer.user = userNode
          ? {
              userID: userNode.userID,
              loginStatus,
              gender: userNode.gender,
              birthDate: userNode.birthDate,
              location: userNode.location,
            }
          : { loginStatus };
      }

      window.objDataLayer = objDataLayer;

      return objDataLayer;
    },
    [authUser, userNode, platform]
  );

  const dispatchDataLayer = useCallback(
    (eventType: string, options: DataLayerOptions = {}) => {
      const objDataLayer = buildDataLayer(eventType, options);

      document.dispatchEvent(
        new CustomEvent(eventType, { detail: objDataLayer })
      );

      return objDataLayer;
    },
    [buildDataLayer]
  );

  const dispatchPageView = useCallback(
    (totalResults: number) => {
      return dispatchDataLayer("customPageView", {
        dataLayer: {
          listItems: { totalResults: String(totalResults) },
        },
      });
    },
    [dispatchDataLayer]
  );

  const dispatchFavoriteOff = useCallback(
    (vehicle: FavoriteOffVehicle) => {
      return dispatchDataLayer("favoriteOff", {
        dataLayer: {
          listItems: { totalResults: "0" },
          ad: { id: String(vehicle.advertiseId) },
          dealer: { id: String(vehicle.advertiserId ?? "") },
          vehicle: {
            make: { name: vehicle.brand ?? "", id: String(vehicle.brandId ?? "") },
            model: { name: vehicle.model ?? "", id: String(vehicle.modelId ?? "") },
            version: { name: vehicle.version ?? "", id: String(vehicle.versionId ?? "") },
          },
        },
      });
    },
    [dispatchDataLayer]
  );

  return {
    dispatchDataLayer,
    dispatchPageView,
    dispatchFavoriteOff,
    isUserDataReady,
  };
}
